import { isEmpty } from "@swiggy-private/core-ui";
import {
    COUPON_GROUP_TYPES,
    ApplyCouponsWidgetInfo,
    ReviewedCartApiResponse,
    GiftingCartApiResponse,
    GiftingCouponCode,
    ICouponCard,
} from "@swiggy-private/cart-types";
import { DOUBLE_OFFER_COUPON_TYPE } from "@cart-ui/constants/Coupon";
import { CouponCardType } from "@swiggy-private/dls-web-recipe-common-web/dist/components/CouponCard";

export const getCouponExpiryInDays = (expiryInMs: string): number => {
    const oneDayInMs = 1000 * 60 * 60 * 24;
    return Number(expiryInMs ?? "") / oneDayInMs;
};

// This is applicable only for scenario where expiring cashback is applicable on the order
// and the order is under valued to redeem the entire cashback amount
export const getUnderUtilisationText = (underUtilisationMeta?: [{ text: string }]): string | undefined => {
    if (!underUtilisationMeta || isEmpty(underUtilisationMeta) || isEmpty(underUtilisationMeta[0].text)) return;
    return underUtilisationMeta[0].text;
};

export const isBestCouponGroup = (type: COUPON_GROUP_TYPES): boolean => {
    return type === COUPON_GROUP_TYPES.BEST_COUPON;
};

export const isGreatDealCouponGroup = (type: COUPON_GROUP_TYPES) => {
    return type === COUPON_GROUP_TYPES.GREAT_DEALS;
};

export const isDoubleOfferCouponGroup = (type?: string): boolean => {
    return type === DOUBLE_OFFER_COUPON_TYPE;
};

export const isPaymentOfferCouponGroup = (type: COUPON_GROUP_TYPES): boolean => {
    return type === COUPON_GROUP_TYPES.PAYMENT_OFFERS;
};

export const isAppliedCouponGroup = (type: COUPON_GROUP_TYPES): boolean => {
    return type === COUPON_GROUP_TYPES.APPLIED_OFFERS;
};

export const isRecommendedCouponGroup = (type: COUPON_GROUP_TYPES): boolean => {
    return type === COUPON_GROUP_TYPES.RECOMMENDED_OFFERS;
};

export const getTransformedHtmlString = (value?: string): string | undefined => {
    // HACK: This implementation of replacing /n with <br> is required to backward-support app side implementation
    // and should be removed once coupon listing is 100% migrated to web
    return value?.replace(/\n/g, "<br>");
};

export const getAppliedRestaurantCouponCode = (
    cartResponseData?: ReviewedCartApiResponse | null,
): string | undefined => {
    return cartResponseData?.coupon_details?.coupon_codes?.restaurant_coupon;
};

export const getAppliedPaymentCouponCode = (cartResponseData?: ReviewedCartApiResponse | null): string | undefined => {
    return cartResponseData?.coupon_details?.coupon_codes?.payment_coupon;
};

export const getRestaurantCouponCode = (
    cartResponseData?: ReviewedCartApiResponse | null,
    applyCouponWidgetData?: ReviewedCartApiResponse["apply_coupon_widget_info"],
): string | undefined => {
    return getAppliedRestaurantCouponCode(cartResponseData) || applyCouponWidgetData?.coupon_code;
};

export const getAppliedGiftingCouponCodes = (
    cartResponseData?: GiftingCartApiResponse | null,
): GiftingCouponCode[] | undefined => {
    return cartResponseData?.couponCodes;
};

export const getPaymentCouponCode = (
    cartResponseData?: ReviewedCartApiResponse | null,
    applyPaymentCouponWidgetData?: ApplyCouponsWidgetInfo["payment_coupon"],
): string | undefined => {
    return getAppliedPaymentCouponCode(cartResponseData) || applyPaymentCouponWidgetData?.coupon_code;
};

export const getCouponListingType = (isPaymentCouponCard?: boolean): CouponCardType => {
    return isPaymentCouponCard ? CouponCardType.PAYMENT_COUPON_CARD : CouponCardType.RESTAURANT_COUPON_CARD;
};

export const getDefaultSelectedCoupon = (
    coupons?: ICouponCard[] | null,
): { coupon: string; applicableType: string }[] => {
    if (!coupons) return [];

    return coupons.reduce((selectedCoupons, card) => {
        if (isEmpty(card.coupons)) return selectedCoupons;

        const cardSelectedCoupons = card?.coupons
            .filter(coupon => coupon.isSelected)
            .map(coupon => ({
                coupon: coupon.title || "",
                applicableType: coupon.businessLine || "",
            }));

        return [...selectedCoupons, ...cardSelectedCoupons];
    }, [] as { coupon: string; applicableType: string }[]);
};

export const getDefaultSelectedOfferIdlist = (coupons?: ICouponCard[] | null): string[] => {
    return (
        coupons?.flatMap(
            card => card.coupons?.filter(coupon => coupon.isSelected).map(coupon => coupon.id as string) || [],
        ) || []
    );
};
